import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import WebApp from '@twa-dev/sdk'
import cn from 'classnames'

import { UserStore } from 'stores'
import { FlexLayout } from 'layouts'
import { useValueContext, ValueContext, analyticsContext } from 'contexts'
import { PageSpinner, Avatar, Button, Link, Spinner } from 'components'
import { calculateAge, getNotificationName, useApp } from 'utils'
import { IBaby, IBabyNotification } from 'types'
import { botUrl } from 'consts'

import defaultAvatar from './defaultAvatar.png'
import { EditBlock } from './components'
import './styles.scss'

export const User = observer(() => {
  const { subscription } = useApp()
  const [store] = useState(() => new UserStore(WebApp.initDataUnsafe.user))

  const navigate = useNavigate()

  const {
    babies,
    babyNotifications,
    selectedBaby,
    loading: notificationsLoading,
    setSelectedBaby,
    setEditableBaby,
    setEditableNotification,
    loadBabyNotifications,
  } = useValueContext()

  const { loading, firstName, lastName } = store
  const isPremium = subscription?.hasActiveSubscription

  const handleBabyClick = useCallback(
    (baby: IBaby) => {
      if (baby) {
        setSelectedBaby({ ...baby })
      }
    },
    [setSelectedBaby],
  )

  const handleBabyEdit = (baby: IBaby) => {
    if (baby) {
      setEditableBaby({ ...baby })
      navigate('/baby/edit')
    }
  }

  useEffect(() => {
    if (selectedBaby?.id) {
      loadBabyNotifications()
    }
  }, [loadBabyNotifications, selectedBaby?.id])

  const handleNotificationEdit = (notification: IBabyNotification) => {
    if (notification) {
      setEditableNotification({ ...notification })
      navigate('/baby/notification/edit')
    }
  }

  const onFeedbackClick = (e: React.MouseEvent) => {
    e.preventDefault()
    WebApp.openTelegramLink(botUrl)
  }

  const onPremiumClick = () => {
    analyticsContext.trackEvent('account_premium_clicked')
    navigate('/premium')
  }

  return (
    <ValueContext.Provider value={{ store }}>
      <PageSpinner delay={400} minVisibleTime={400} open={loading} />
      <FlexLayout.Container
        className="profile-screen with-mobile-indent with-menu-padding"
        vertical
      >
        <div className="scrollable-layout">
          <div className="user-data">
            <Avatar
              src={store.avatar || defaultAvatar}
              alt={`${store.fullName}'s avatar`}
              size={48}
            />
            <div className="user-name">
              {firstName} {lastName}
            </div>
          </div>

          {subscription && !isPremium && (
            <Button className="premium-btn" onClick={onPremiumClick}>
              Купить 🌟<b>Премиум Аккаунт</b>🌟
            </Button>
          )}
        </div>

        <FlexLayout.Content className="scrollable-layout" scrollable>
          <div className="children-block">
            <div className="title">Дети</div>

            {babies.map((baby: IBaby) => {
              const age = calculateAge(baby.birthDate)

              return (
                <EditBlock
                  key={baby.id}
                  onClick={() => handleBabyClick(baby)}
                  onEdit={() => handleBabyEdit(baby)}
                  className="child"
                >
                  <div className="child-data">
                    <div className={cn('name', { selected: selectedBaby?.id === baby.id })}>
                      {baby.name}
                    </div>
                    {age && <div className="age">{age}</div>}
                  </div>
                </EditBlock>
              )
            })}
          </div>

          <div className="reminders">
            <div className="title">Напоминания</div>
            {notificationsLoading ? (
              <Spinner variant="sm" open />
            ) : (
              babyNotifications.map((notification: IBabyNotification) => {
                const onEdit = () => handleNotificationEdit(notification)

                return (
                  <EditBlock
                    key={notification.id}
                    onClick={onEdit}
                    onEdit={onEdit}
                    className="reminder"
                  >
                    <div className="reminder-name">{getNotificationName(notification.tag)}</div>
                  </EditBlock>
                )
              })
            )}
          </div>
          <div className="feedback-btn">
            <Link onClick={onFeedbackClick} prependIcon="💬">
              Поделиться
              <br />
              обратной связью
            </Link>
          </div>
        </FlexLayout.Content>
      </FlexLayout.Container>
    </ValueContext.Provider>
  )
})
